import { useState, useEffect } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { Redirect } from 'react-router-dom';

import ClientInfo from './ClientInfo';
import CreateCustomer from './CreateCustomer';
import { Backdrop } from '../../components/Backdrop';
import RequestProductionMessage from '../../components/RequestProductionAccessMsg';
import useApp from '../../hooks/useApp';
import useTeam from '../../hooks/useTeam';
import { getErrorMessage } from '../../lib/error';
import { hasCustomerInfoScope, isValidJwt } from '../../lib/jwt';
import Routes from '../../lib/routes';
import { CustomerInfo, getSDKClient } from '../../lib/sdk';

const isUnauthorisedError = (error: unknown) => {
  return axios.isAxiosError(error) && error.response?.status === 401;
};

export default function ApplicationSettings(): JSX.Element {
  const { app } = useApp();
  const { team } = useTeam();
  const { enqueueSnackbar } = useSnackbar();
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [requirePermission, setRequirePermission] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loggedOut, setLoggedOut] = useState<boolean>(false);
  const [token, setToken] = useState<string>('');

  const refreshClientSecret = async () => {
    try {
      const sdk = getSDKClient(app.env, String(token));
      if (customerInfo !== null) {
        const customerAppId = customerInfo?.customer_app_id;
        const secret = await sdk.refreshClientSecret(customerAppId);
        if (secret !== null) {
          const secrets = await sdk.getCustomerSecret(customerAppId);
          if (secrets !== null) {
            const prefixes = secrets.map((item) => item.client_secret_prefix);
            setCustomerInfo({ ...customerInfo, client_secret: secret.client_secret, prefixes });
          }
          enqueueSnackbar('Successfully generated client secret.', { variant: 'success' });
        }
      }
    } catch (err) {
      if (isUnauthorisedError(err)) {
        setLoggedOut(true);
      }
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  };

  useEffect(() => {
    async function updateCustomerInfo(env: string, token: string) {
      try {
        setIsLoading(true);
        const sdk = getSDKClient(env, token);
        const customerInfo = await sdk.getCustomerInfo(team.team_id);
        setRequirePermission(false);

        if (customerInfo === null) {
          setCustomerInfo(null);
          setIsLoading(false);
          return;
        }

        const customerAppId = customerInfo.customer_app_id;
        const secrets = await sdk.getCustomerSecret(customerAppId);
        if (secrets !== null) {
          const prefixes = secrets.map((item) => item.client_secret_prefix);
          setCustomerInfo({ ...customerInfo, prefixes });
        }
        setIsLoading(false);
      } catch (err) {
        if (isUnauthorisedError(err)) {
          setLoggedOut(true);
        }
        enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
        setIsLoading(false);
      }
    }

    const { tokens, env } = app;
    const currentToken = tokens.get(env) ?? '';

    setToken(currentToken);

    if (hasCustomerInfoScope(currentToken)) {
      updateCustomerInfo(env, currentToken);
    } else {
      if (isValidJwt(currentToken)) {
        setRequirePermission(true);
      }
    }
  }, [app, team.team_id, enqueueSnackbar]);

  if (loggedOut) {
    return <Redirect to={Routes.Logout} />;
  }

  // If the customer does not have to permissions to manage the api
  if (requirePermission) {
    return <RequestProductionMessage />;
  }

  if (isLoading === true) {
    return (
      <Backdrop open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  // If there is existing customer we show client info, otherwise we should create customer component
  if (customerInfo === null) {
    return (
      <>
        <CreateCustomer />
      </>
    );
  }

  // This is used for displaying customer overview.
  return (
    <>
      <ClientInfo customerInfo={customerInfo} refreshClientSecret={refreshClientSecret} />
    </>
  );
}
